import React, { useRef, useState, useEffect } from 'react';
import {
    Box
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import ReusableHeadingComponents from '../../components/reusableComponents/reusableHeadingComponents';
import ReusableButton from '../../components/reusableComponents/reusableButtonComponents';
import ReusableMuiIcons from '../../_helpers/ReusableMuiIcons';
import SearchInput from '../../components/reusableComponents/reusableSearchInput';
import Tablecomponent from '../../components/reusableComponents/Tablecomponent';
import GenerateColumns from '../../_helpers/reusablefunctions/GenerateColumns';
import ApiService from '../../_services/ApiService';
import ReusableChip from '../../components/reusableComponents/chipSelector';
import { formatDistanceToNow } from 'date-fns';
import ConfirmMessageBox from '../../components/reusableComponents/confirmMessageBox';
import useDebounce from '../../_helpers/reusablefunctions/useDebounce';
import { TableSkeletonRows } from '../../components/reusableComponents/SkeletonCard';

const ViewDraft = () => {
    const history = useHistory();

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState([]);
    const [draftData, setDraftData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmOpen, setConfirmOpen] = useState(false);
    // const [confirmType, setConfirmType] = useState('multiple');
    const [tempDeleteIds, setTempDeleteIds] = useState([]);
    const [count , setCount] = useState([]);

    const isAllSelected = draftData.length > 0 && selectedRows.length === draftData.length;
    const isIndeterminate = selectedRows.length > 0 && selectedRows.length < draftData.length;
    const selectAllRef = useRef();

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = isIndeterminate;
        }
    }, [isIndeterminate]);

    useEffect(() => {
        const fetchDrafts = async () => {
            setLoading(true);
            const params = { is_draft: true , limit: rowsPerPage, offset: page * rowsPerPage,};

            if (debouncedSearchTerm) {
                params.search = debouncedSearchTerm;
            }

            try {
                const res = await ApiService.get("all_campaigns", params);
                setDraftData(res?.data?.results || []);
                setCount(res?.data?.count)
            } catch (error) {
                console.error("Failed to fetch draft offers", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDrafts();
    }, [debouncedSearchTerm ,rowsPerPage , page]);

    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedRows([]);
        } else {
            const allIds = draftData.map((row) => row.id);
            setSelectedRows(allIds);
        }
    };

    const handleSelectRow = (id) => {
        setSelectedRows((prev) =>
            prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
        );
    };

    const handleCreateOffer = () => history.push('/new-offer/campaign-type');
    const handleBack = () => history.push('/new-offer');

    const handleDeleteSelected = async (ids = selectedRows, type = "multiple") => {
        if (!ids.length) return;
        setTempDeleteIds(ids);
        // setConfirmType(type);
        setConfirmOpen(true);
    };

    const handleConfirmSubmit = async () => {
        const idsParam = tempDeleteIds.join(",");
        try {
            await ApiService.delete('barterCampaign', { ids: `${idsParam}` });
            setDraftData((prevData) => prevData.filter(item => !tempDeleteIds.includes(item.id)));
            setSelectedRows((prev) => prev.filter(id => !tempDeleteIds.includes(id)));
        } catch (error) {
            console.error('Failed to delete draft offer(s):', error);
        } finally {
            setConfirmOpen(false);
        }
    };

    const draftColumnConfig = [
        {
            field: 'checkbox',
            headerName: '',
            type: 'custom',
            minWidth: 60,
            renderHeaderComponent: () => (
                <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={selectAllRef}
                    onChange={handleSelectAll}
                />
            ),
            renderComponent: ({ row }) => (
                <input
                    type="checkbox"
                    checked={selectedRows.includes(row.id)}
                    onChange={() => handleSelectRow(row.id)}
                />
            )
        },
        {
            field: 'title',
            headerName: 'Offer Title',
            type: 'text',
            // minWidth: 250
        },
        {
            field: 'campaign_type', headerName: "Type", type: "custom",
            renderComponent: ({ value, row }) => <ReusableChip label={row?.campaign_type?.campaign_name} />
        },
        {
            field: 'updated_at',
            headerName: 'Last Modified',
            type: 'custom',
            // minWidth: 150,
            renderComponent: ({ row }) => {
                const date = new Date(row?.updated_at);
                if (isNaN(date)) return '--';
                return formatDistanceToNow(date, { addSuffix: true });
            }
        },
        {
            field: 'actions',
            headerName: '',
            type: 'custom',
            minWidth: 200,
            renderComponent: ({ row }) => (
                <Box sx={{ display: selectedRows.length > 0 ? 'none' : 'block' }} >
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: "flex-end", ml: "auto" }}>
                        <ReusableButton
                            children="Continue"
                            variant="contained"
                            classNames="gn-actionbtn"
                            onClick={() => history.push(`/edit-offer/overview/${row.id}`)}
                        />
                        <ReusableButton
                            children="Delete"
                            variant="outlined"
                            classNames="gn-actionbtn-outline gn-actionbtn-color me-3"
                            onClick={() => handleDeleteSelected([row.id], "single")}
                        />
                    </Box>
                </Box>
            )
        }
    ];

    const draftColumns = GenerateColumns(draftColumnConfig);

    return (
        <Box p={4} pb={8}>
            <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <ReusableHeadingComponents
                    bacHistory={true}
                    clickHandlerBack={handleBack}
                    heading="Draft Offers"
                    subHeading="Effortlessly Create, Manage & Track Your Brand Offers."
                />
                <ReusableButton
                    children="Create new offer"
                    variant="contained"
                    classNames="gn-actionbtn"
                    onClick={handleCreateOffer}
                    startIcon={<ReusableMuiIcons name="add" />}
                />
            </Box>

            <Box sx={{ mb: 2 }}>
                <SearchInput
                    placeholder="Search Offers"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: '35%' }}
                />
            </Box>

            <Box mb={4}>
                {loading ? (
                    <TableSkeletonRows />
                ) : (<Tablecomponent
                    columns={draftColumns}
                    data={draftData}
                    page={page}
                    count={count}
                    setPage={setPage}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 20, 30]}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    onDeleteSelected={() => handleDeleteSelected(selectedRows, 'multiple')}
                    bulkActions={['delete']}
                    message={"No Draft Offers Yet"}
                    content={"Start by creating your first campaign — choose from barter, affiliate, store invite, or ambassador types."}
                />)}
            </Box>
            <ConfirmMessageBox
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onSubmit={handleConfirmSubmit}
                // title={
                //     confirmType === "single"
                //         ? "This draft will be permanently deleted."
                //         : "All selected drafts will be permanently deleted."
                // }
                // message={
                //     confirmType === "single"
                //         ? "Deleted draft cannot be recovered."
                //         : "Deleted drafts cannot be recovered."
                // }
                children="Delete"
                heading="Are you sure you want to delete?"
                subHeading="This action is irreversible and the draft offer(s) will be permanently removed."
            />

        </Box>
    );
};

export default ViewDraft;
